/**
 * App config service — environment switches the SPA needs before/without auth.
 *
 * Fetches the public GET /api/config (anonymous-readable). The result is cached in
 * localStorage so the value is warm on subsequent loads (including static pages that
 * don't subscribe to React state). UI gating only — the server is the real boundary.
 */

const ROLES_ENABLED_CACHE_KEY = 'is_app_roles_enabled';
const ANON_LIMITS_CACHE_KEY = 'is_app_anonymous_limits';
const DRAFT_TIMINGS_CACHE_KEY = 'is_app_draft_timings';
const BILLING_CACHE_KEY = 'is_app_billing';
const MAX_MEGAPIXELS_CACHE_KEY = 'is_app_max_megapixels';

/**
 * The Guest tier's published allowance, from the server.
 *
 * Mirrored into localStorage for the same reason as `roles_enabled`: it must be warm on the
 * very first render, before the config fetch resolves, or a logged-out visitor sees their
 * slice count with no ceiling and then watches it change. `null` fields mean "server did not
 * say" — the UI degrades to a plain count rather than inventing a number.
 */
export type AnonymousLimits = {
  max_slices_per_image: number | null;
  max_file_size_bytes: number | null;
};

/**
 * Draft auto-save timing from config/drafts.php. `null` means the server did not say, and the
 * hook falls back to its own defaults.
 */
export type DraftTimings = {
  autosave_debounce_ms: number | null;
  autosave_max_wait_ms: number | null;
  save_timeout_ms: number | null;
  retry_delays_ms: number[] | null;
  /** Easter egg: Save clicks in a row with nothing new before the thank-you line, and how long it shows. */
  save_thanks_clicks: number | null;
  save_thanks_ms: number | null;
};

/**
 * Billing facts the marketing copy needs. `trial_days` is the number the server actually passes to
 * Stripe (config/billing.php), so the pricing page can state the trial instead of hardcoding a
 * figure that drifts from what a customer is really granted. 0 = no trial; say nothing.
 */
export type BillingConfig = {
  trial_days: number;
  /** The Founding Member deal is on: the note shows, and new subscriptions are tagged. */
  founding_deal: boolean;
};

export type AppConfig = {
  roles_enabled: boolean;
  anonymous_limits: AnonymousLimits;
  draft_timings: DraftTimings;
  billing: BillingConfig;
  /**
   * The server's image-dimension limit (config/images.php), checked by the editor the moment an
   * image is picked or added as a layer. null = the server did not say; the upload still checks.
   */
  max_megapixels: number | null;
};

const EMPTY_ANON_LIMITS: AnonymousLimits = {
  max_slices_per_image: null,
  max_file_size_bytes: null,
};

const EMPTY_DRAFT_TIMINGS: DraftTimings = {
  autosave_debounce_ms: null,
  autosave_max_wait_ms: null,
  save_timeout_ms: null,
  retry_delays_ms: null,
  save_thanks_clicks: null,
  save_thanks_ms: null,
};

/** Coerce one limit field: a positive number, else null. Never a guess. */
function readLimit(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** Coerce draft timings; a retry list with any invalid entry is ignored as a whole. */
function readDraftTimings(raw: any): DraftTimings {
  const delays = Array.isArray(raw?.retry_delays_ms) ? raw.retry_delays_ms.map(readLimit) : null;
  return {
    autosave_debounce_ms: readLimit(raw?.autosave_debounce_ms),
    autosave_max_wait_ms: readLimit(raw?.autosave_max_wait_ms),
    save_timeout_ms: readLimit(raw?.save_timeout_ms),
    retry_delays_ms: delays && delays.every((d: number | null) => d !== null) ? delays : null,
    save_thanks_clicks: readLimit(raw?.save_thanks_clicks),
    save_thanks_ms: readLimit(raw?.save_thanks_ms),
  };
}

/** Last-known roles_enabled from a prior fetch (warm across reloads). Defaults false. */
export function getCachedRolesEnabled(): boolean {
  try {
    return window.localStorage.getItem(ROLES_ENABLED_CACHE_KEY) === 'true';
  } catch (_e) {
    return false;
  }
}

/** Last-known Guest limits from a prior fetch. All-null until the first response. */
export function getCachedAnonymousLimits(): AnonymousLimits {
  try {
    const raw = window.localStorage.getItem(ANON_LIMITS_CACHE_KEY);
    if (!raw) return EMPTY_ANON_LIMITS;
    const parsed = JSON.parse(raw);
    return {
      max_slices_per_image: readLimit(parsed?.max_slices_per_image),
      max_file_size_bytes: readLimit(parsed?.max_file_size_bytes),
    };
  } catch (_e) {
    return EMPTY_ANON_LIMITS;
  }
}

/** Last-known draft auto-save timings from a prior fetch. All-null until the first response. */
export function getCachedDraftTimings(): DraftTimings {
  try {
    const raw = window.localStorage.getItem(DRAFT_TIMINGS_CACHE_KEY);
    return raw ? readDraftTimings(JSON.parse(raw)) : EMPTY_DRAFT_TIMINGS;
  } catch (_e) {
    return EMPTY_DRAFT_TIMINGS;
  }
}

const EMPTY_BILLING: BillingConfig = { trial_days: 0, founding_deal: false };

/** Fail closed: an unreadable value means "no trial" and "no deal", so copy never promises wrongly. */
function readBilling(raw: unknown): BillingConfig {
  const r = raw as { trial_days?: unknown; founding_deal?: unknown } | null;
  const days = Number(r?.trial_days);
  return {
    trial_days: Number.isFinite(days) && days > 0 ? Math.floor(days) : 0,
    founding_deal: r?.founding_deal === true,
  };
}

/**
 * Last-known trial length. Cached like the other config so the pricing page states it on the
 * FIRST paint rather than popping it in once /api/config answers.
 */
export function getCachedBilling(): BillingConfig {
  try {
    const raw = window.localStorage.getItem(BILLING_CACHE_KEY);
    return raw ? readBilling(JSON.parse(raw)) : EMPTY_BILLING;
  } catch (_e) {
    return EMPTY_BILLING;
  }
}

/** Last-known megapixel limit from a prior fetch; null until the first response. */
export function getCachedMaxMegapixels(): number | null {
  try {
    const value = Number(window.localStorage.getItem(MAX_MEGAPIXELS_CACHE_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (_e) {
    return null;
  }
}

export async function fetchAppConfig(): Promise<AppConfig> {
  const resp = await fetch('/api/config', {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (!resp.ok) {
    throw new Error('Failed to load app config');
  }

  const data = await resp.json();
  const rolesEnabled = Boolean(data?.roles_enabled);
  const anonymousLimits: AnonymousLimits = {
    max_slices_per_image: readLimit(data?.anonymous_limits?.max_slices_per_image),
    max_file_size_bytes: readLimit(data?.anonymous_limits?.max_file_size_bytes),
  };
  const draftTimings = readDraftTimings(data?.drafts);
  const billing = readBilling(data?.billing);
  const maxMegapixels = Number(data?.max_megapixels) > 0 ? Number(data.max_megapixels) : null;

  try {
    window.localStorage.setItem(ROLES_ENABLED_CACHE_KEY, rolesEnabled ? 'true' : 'false');
    window.localStorage.setItem(ANON_LIMITS_CACHE_KEY, JSON.stringify(anonymousLimits));
    window.localStorage.setItem(DRAFT_TIMINGS_CACHE_KEY, JSON.stringify(draftTimings));
    window.localStorage.setItem(BILLING_CACHE_KEY, JSON.stringify(billing));
    if (maxMegapixels !== null) window.localStorage.setItem(MAX_MEGAPIXELS_CACHE_KEY, String(maxMegapixels));
  } catch (_e) {
    // ignore
  }

  return {
    roles_enabled: rolesEnabled,
    anonymous_limits: anonymousLimits,
    draft_timings: draftTimings,
    billing,
    max_megapixels: maxMegapixels
  };
}
